import { Button, useArtistContext } from "Components";
import { FlexColumn, FlexRow } from "Components/Flex";
import { H2, H3, H4, Li, LinkText } from "melodies-source/Text";
import styled from "styled-components";
import { Card } from "melodies-source/Card";
import { useState } from "react";
import DropdownMenu, {
  DropdownContainer,
  DropdownOption,
} from "Components/DropdownMenu";
import { SvgLink } from "melodies-source/Svgs/Link";
import { SvgMoreLarge } from "melodies-source/Svgs/MoreLarge";
import { ReactComponent as SvgCircleChecked } from "assets/svg/circle-checked.svg";
import { ReactComponent as SvgCircleBlock } from "assets/svg/circle-block.svg";
import { ReactComponent as SvgClipboardEdit } from "assets/svg/clipboard-edit.svg";
import { ReactComponent as SvgRefresh } from "assets/svg/refresh.svg";
import { ReactComponent as SvgStar } from "./SvgStar.svg";
import { ReactComponent as SvgFeatured } from "./SvgFeatured.svg";
import { ReactComponent as SvgFileText } from "assets/svg/file-text.svg";
import { ReactComponent as AddUserIcon } from "assets/svg/add-user.svg";
import { SvgIndividual } from "melodies-source/Svgs/Individual";
import { DateTime } from "luxon";
import { SvgGear } from "melodies-source/Svgs/Gear";
import { SvgDelete } from "melodies-source/Svgs/Delete";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useHistory } from "react-router-dom";
import { AdditionalGroupsAvatars } from "Components/AdditionalGroups";
import { GetLinkModal } from "./GetLinkModal";
import { formatNumber } from "Utils/format";
import { Typesense } from "@max/common/typesense";
import { useAdditionalArtists } from "Routes/SetFan/hooks/useAdditionalArtists";

const SurveyMenuOptions = [
  "fanView",
  "editSurvey",
  "setFeatured",
  "endSurvey",
  "resumeSurvey",
  "deleteSurvey",
];
export type SurveyMenuOption = (typeof SurveyMenuOptions)[number];

type SurveyTypesenseDocument = Typesense<"surveys">;

export const SurveyCard = ({
  survey,
  onSurveyMenuSelect,
  hasFeaturedSupport,
  featuredId,
  featuredUrl,
  onPublish,
}: {
  survey: SurveyTypesenseDocument;
  onSurveyMenuSelect: (
    val: SurveyMenuOption,
    survey: SurveyTypesenseDocument,
  ) => void;
  hasFeaturedSupport: boolean;
  featuredId: string;
  featuredUrl?: string;
  onPublish: (survey: SurveyTypesenseDocument) => void;
}) => {
  const { id: artistGroupId, isAdmin, isGlobalAdmin } = useArtistContext();
  const [getLinkModalOpen, setGetLinkModalOpen] = useState(false);
  const history = useHistory();

  const isActive = ["published", "live", "completed"].includes(survey.status);

  const color = isActive ? "#1B0076" : "#999";
  const hasResults = survey.responses > 0;
  const isFeatured = survey.id === featuredId;
  const { additionalGroups, isAdditional } = useAdditionalArtists(
    artistGroupId,
    survey,
  );

  const surveyMenuOptions = isAdditional // if artist is an `additional` artist, they are read only
    ? [fanView]
    : SurveyMenu(
        survey,
        hasFeaturedSupport,
        featuredId,
        isAdmin,
        isAdditional,
        isGlobalAdmin,
      );

  return (
    <StyledSurveyCard isElevated>
      {surveyMenuOptions.length > 0 && (
        <SurveyDropdownMenu
          disableAnimation
          options={surveyMenuOptions}
          icon={<SvgMoreLarge />}
          onClick={(selection) => onSurveyMenuSelect(selection, survey)}
          forceSide="left"
        />
      )}
      <SurveyIconContainer>
        <SvgClipboardEdit />
      </SurveyIconContainer>
      <SurveyCardContent>
        <H2>{survey.internalTitle || survey.title}</H2>
        <FlexRow style={{ marginBottom: 5 }}>
          <H3>
            {!!survey.responses && (
              <>
                {formatNumber(survey.responses)} Response
                {survey.responses === 1 ? "" : "s"} •
              </>
            )}
            {survey.responses === 0
              ? " Awaiting Responses"
              : ` Last Response: ${DateTime.fromMillis(
                  survey.lastResponse,
                ).toRelative()}`}
          </H3>
        </FlexRow>
        <FlexRow style={{ alignItems: "center" }}>
          <SurveyStatusComp status={survey.status ?? "unpublished"} />
          {isFeatured && <SvgFeatured style={{ marginLeft: "10px" }} />}
        </FlexRow>
      </SurveyCardContent>
      {!!additionalGroups.length && (
        <AdditionalGroups>
          <AdditionalGroupsAvatars ids={additionalGroups} type="campaign" />
        </AdditionalGroups>
      )}
      <SurveyCardActions>
        <Button
          onClick={async () => {
            if (isActive) {
              history.push(
                `/${artistGroupId}/reporting/set-fan/surveys/${survey.surveyId}`,
              );
            } else {
              if (!isAdditional) onPublish(survey);
            }
          }}
          variant="primary"
          style={{ marginBottom: 10, width: 156 }}
          disabled={(isActive && !hasResults) || (!isActive && isAdditional)}
        >
          {isActive ? "View Results" : "Publish"}
        </Button>
        <FlexRow
          style={{
            alignItems: "center",
            cursor: isActive ? "pointer" : "not-allowed",
          }}
        >
          <SvgLink
            style={{
              height: 16,
              width: 16,
              transform: "rotate(-45deg)",
              color,
              marginRight: 2,
            }}
          />
          <LinkText
            as="a"
            style={{
              color,
              textDecoration: "none",
              pointerEvents: !isActive ? "none" : "auto",
            }}
            onClick={() => setGetLinkModalOpen(true)}
          >
            Get Link
          </LinkText>
          <GetLinkModal
            isOpen={getLinkModalOpen}
            onClose={() => setGetLinkModalOpen(false)}
            surveyId={survey.id}
            featuredUrl={isFeatured ? featuredUrl : null}
          />
        </FlexRow>
      </SurveyCardActions>
    </StyledSurveyCard>
  );
};

export const SurveyCardLoader = () => (
  <StyledSurveyCard isElevated>
    <Skeleton circle width={88} height={88} style={{ marginRight: 30 }} />
    <FlexColumn style={{ marginRight: "auto" }}>
      <Skeleton
        width={300}
        count={3}
        wrapper={(props) => (
          <div style={{ lineHeight: "1.5rem" }}>{props.children}</div>
        )}
      />
    </FlexColumn>
    <Skeleton height={78} width={150} />
  </StyledSurveyCard>
);

const SurveyDropdownMenu = styled(DropdownMenu)`
  position: absolute;
  top: 12px;
  right: 11px;

  ${DropdownContainer} {
    width: max-content;
  }

  ${DropdownOption} {
    padding-right: 25px;

    svg {
      color: var(--text-color);
    }
  }
`;

const fanView = {
  icon: <SvgIndividual style={{ marginRight: 12 }} />,
  label: "Fan View",
  value: "fanView",
};
const addGroup = {
  label: "Add Artist",
  icon: (
    <AddUserIcon
      color="var(--text-color)"
      style={{ marginRight: 12, marginLeft: -3 }}
    />
  ),
  value: "addGroup",
};
const editSurvey = {
  icon: <SvgGear style={{ marginRight: 12 }} />,
  label: "Edit",
  value: "editSurvey",
};
const endSurvey = {
  icon: <SvgCircleBlock style={{ marginRight: 12 }} />,
  label: "End Campaign",
  value: "endSurvey",
};
const resumeSurvey = {
  icon: <SvgRefresh style={{ marginRight: 12 }} />,
  label: "Resume Campaign",
  value: "resumeSurvey",
};
const deleteSurvey = {
  icon: <SvgDelete style={{ marginRight: 12, color: "#E45C52" }} />,
  label: "Delete",
  value: "deleteSurvey",
  labelStyles: ["color: #E45C52"],
};
const setFeatured = {
  icon: <SvgStar style={{ marginRight: 12 }} />,
  label: "Feature This Campaign",
  value: "setFeatured",
};

const SurveyMenu = (
  survey: SurveyTypesenseDocument,
  hasFeaturedSupport: boolean,
  featuredId: string,
  isAdmin: boolean,
  isAdditional: boolean,
  isGlobalAdmin: boolean,
) => {
  const hasPermissions =
    !survey.isRestrictedToAdmins || (survey.isRestrictedToAdmins && isAdmin);

  let extras = [];
  if (hasFeaturedSupport && featuredId !== survey.id) {
    extras.push(setFeatured);
  }
  let menu = [
    fanView,
    hasPermissions && editSurvey,
    hasPermissions && endSurvey,
    ...extras,
    hasPermissions && deleteSurvey,
  ];

  if (["draft", "unpublished"].includes(survey.status)) {
    menu = hasPermissions ? [editSurvey, deleteSurvey] : [];
  }

  if (["completed", "cancelled"].includes(survey.status)) {
    menu = hasPermissions
      ? [fanView, editSurvey, resumeSurvey, deleteSurvey]
      : [fanView];
  } else {
    // if artist group created survey, allow `add group`
    if (!isAdditional && isGlobalAdmin) menu.splice(1, 0, addGroup);
  }

  menu = menu.filter((m) => m);
  return menu;
};

const SurveyStatusComp = ({
  status,
}: {
  status: SurveyTypesenseDocument["status"];
}) => {
  switch (status) {
    case "draft":
    case "unpublished":
      return (
        <>
          <SvgFileText style={{ marginRight: 3 }} />
          <H4 style={{ color: "#FC6716" }}>DRAFT</H4>
        </>
      );
    case "live":
    case "published":
      return (
        <>
          <SvgCircleChecked style={{ color: "#1DAB98", marginRight: 3 }} />
          <H4 style={{ color: "#1DAB98" }}>PUBLISHED</H4>
        </>
      );
    case "completed":
    case "cancelled":
      return (
        <>
          <SvgCircleBlock style={{ marginRight: 3 }} />
          <H4 style={{ color: "#999" }}>CAMPAIGN ENDED</H4>
        </>
      );
  }
};

const SurveyIconContainer = styled(FlexRow)`
  background: #f6f9fb;
  border-radius: 50%;
  margin-right: 30px;

  justify-content: center;
  align-items: center;

  svg path:last-child {
    fill: var(--set-fan-primary);
  }

  ${({ theme }) => theme.media.mobile} {
    width: 75px;
    height: 75px;
    margin-right: 0;
  }
`;

const StyledSurveyCard = styled(Card)`
  padding: 33px 45px;
  display: flex;
  align-items: center;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0px 0px 12px 0px rgba(0, 0, 0, 0.16);
  position: relative;

  ${({ theme }) => theme.media.mobile} {
    padding: 24px 32px;
    flex-direction: column;
    gap: 15px;
  }
`;

const SurveyCardContent = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: auto;
  gap: 4px;
  ${H2} {
    margin-right: 12px;
  }

  ${({ theme }) => theme.media.mobile} {
    align-items: center;
    margin-right: 0;
    ${H2} {
      margin-right: 0;
      font-size: 20px;
      line-height: 28px;
      font-weight: 500;
    }
    ${H3} {
      font-size: 12px;
      line-height: 18px;
      font-weight: 400;
      text-align: center;
    }
  }
`;

const SurveyCardActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  ${({ theme }) => theme.media.mobile} {
    margin-top: 15px;
    width: 100%;

    button {
      width: 100% !important;
    }
  }
`;

export const BulletList = styled.div<{ $iconColor?: string }>`
  display: grid;
  grid-template-columns: 20px 1fr;
  column-gap: 10px;
  row-gap: 16px;
  color: #333333;
  ${Li} {
    line-height: 20px;
  }
  svg {
    color: ${(p) => p.$iconColor};
    width: 20px;
    height: 20px;
  }

  ${({ theme }) => theme.mediaQueries.mobile} {
    column-gap: 8px;
    row-gap: 12px;
    svg {
      width: 18px;
      height: 18px;
    }
    ${Li} {
      font-size: 12px;
      line-height: 18px;
    }
  }
`;

const AdditionalGroups = styled.div`
  margin: 0 20px 0 auto;

  ${(p) => p.theme.media.mobile} {
    margin: 15px 0 0 0;
  }
`;
